import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { bindPropStore } from "../bindProps";
import { cancelScheduledFrame, emit, list, scheduleFrame, text } from "../helpers";
import InlineIcon from "../InlineIcon.svelte";

var root_1 = $.from_html(`<span class="slex-sr-only"> </span>`);
var root_2 = $.from_html(`<button type="button" role="tab"><!> <!></button>`);
var root_3 = $.from_html(`<div class="slex-tabs-content" role="tabpanel"></div>`);
var root_4 = $.from_html(`<div class="slex-tabs"><div class="slex-tabs-list" role="tablist"><!> <span class="slex-tabs-selected-indicator" aria-hidden="true"></span></div> <!></div>`);

export default function Tabs($$anchor, $$props) {
	$.push($$props, true);

	let p = $.state($.proxy({}));
	let value = $.state(undefined);
	let iconVersion = $.state(0);
	const orientation = $.derived(() => text($.get(p).orientation, "horizontal"));
	const items = $.derived(() => list($.get(p).tabs).map((item) => ({ ...item })));
	const triggerClass = "slex-tabs-trigger";

	$.user_effect(() => bindPropStore($$props.props, (next) => {
		$.set(p, next, true);
		$.set(value, next.value, true);
	}));

	function choose(next) {
		$.set(value, next, true);
		emit($$props.ctx, "change", next);
	}

	function chooseIndex(index) {
		const item = $.get(items)[index];

		if (!item || item.disabled) return;

		choose(item.value ?? item.label);
	}

	function handleKeydown(event, index) {
		const enabledItems = $.get(items).map((item, itemIndex) => ({ item, itemIndex })).filter(({ item }) => !item.disabled);
		const currentEnabledIndex = enabledItems.findIndex(({ itemIndex }) => itemIndex === index);

		if (currentEnabledIndex < 0) return;

		let nextEnabledIndex;

		if (event.key === "ArrowRight" || event.key === "ArrowDown") {
			nextEnabledIndex = (currentEnabledIndex + 1) % enabledItems.length;
		} else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
			nextEnabledIndex = (currentEnabledIndex - 1 + enabledItems.length) % enabledItems.length;
		} else if (event.key === "Home") {
			nextEnabledIndex = 0;
		} else if (event.key === "End") {
			nextEnabledIndex = enabledItems.length - 1;
		}

		if (nextEnabledIndex === undefined) return;

		event.preventDefault();

		const nextIndex = enabledItems[nextEnabledIndex]?.itemIndex;

		if (nextIndex === undefined) return;

		chooseIndex(nextIndex);

		queueMicrotask(() => {
			const root = event.currentTarget?.closest(".slex-tabs");
			const trigger = root?.querySelector(`.slex-tabs-trigger[data-value="${CSS.escape(text($.get(items)[nextIndex].value ?? $.get(items)[nextIndex].label))}"]`);

			trigger?.focus();
		});
	}

	function requestIndicatorUpdate() {
		$.set(iconVersion, $.get(iconVersion) + 1);
	}

	function isLayoutContent(value) {
		return !!value && typeof value === "object" && !Array.isArray(value);
	}

	function renderTabPanel(node, item) {
		function render(next) {
			node.replaceChildren();

			if (isLayoutContent(next.content)) {
				$$props.ctx.renderTree(next.content, node, $$props.ctx.forCtx);

				return;
			}

			if (next.content !== undefined && next.content !== null) {
				node.textContent = text(next.content);
			}
		}

		render(item);

		return {
			update(next) {
				render(next);
			},

			destroy() {
				node.replaceChildren();
			}
		};
	}

	function annotateTabs(node) {
		let frame;
		let resizeObserver;
		let indicatorReady = false;
		let destroyed = false;

		function scheduleIndicatorUpdate(list, selectedTrigger, orientationSnapshot) {
			if (destroyed) return;

			cancelScheduledFrame($$props.ctx, frame);

			frame = scheduleFrame($$props.ctx, () => {
				frame = undefined;

				if (destroyed) return;

				if (!selectedTrigger) {
					list.style.setProperty("--slex-tabs-indicator-opacity", "0");

					return;
				}

				const listRect = list.getBoundingClientRect();
				const triggerRect = selectedTrigger.getBoundingClientRect();
				const iconRect = selectedTrigger.querySelector(".slex-tabs-trigger-icon")?.getBoundingClientRect();
				const vertical = orientationSnapshot === "vertical";
				const indicatorStyle = getComputedStyle(list);
				const inlineInset = Number.parseFloat(indicatorStyle.getPropertyValue("--slex-tabs-indicator-inline-inset")) || 0;
				const blockInset = Number.parseFloat(indicatorStyle.getPropertyValue("--slex-tabs-indicator-block-inset")) || 0;
				const iconOnly = !!iconRect && selectedTrigger.classList.contains("slex-tabs-trigger--icon");
				const targetRect = iconOnly && iconRect.width > 0 ? iconRect : triggerRect;

				const width = vertical
					? 2
					: iconOnly
						? Math.max(14, Math.min(22, targetRect.width + 2))
						: Math.max(8, triggerRect.width - inlineInset * 2);

				const height = vertical ? Math.max(8, triggerRect.height - blockInset * 2) : 2;
				const targetCenterX = targetRect.left - listRect.left + targetRect.width / 2;
				const targetCenterY = targetRect.top - listRect.top + targetRect.height / 2;
				const x = vertical ? listRect.width - width : targetCenterX - width / 2;
				const y = vertical ? targetCenterY - height / 2 : listRect.height - height;

				list.style.setProperty("--slex-tabs-indicator-width", `${Math.max(0, width)}px`);
				list.style.setProperty("--slex-tabs-indicator-height", `${Math.max(0, height)}px`);
				list.style.setProperty("--slex-tabs-indicator-x", `${Math.max(0, x)}px`);
				list.style.setProperty("--slex-tabs-indicator-y", `${Math.max(0, y)}px`);
				list.style.setProperty("--slex-tabs-indicator-opacity", "1");

				if (!indicatorReady) {
					indicatorReady = true;

					scheduleFrame($$props.ctx, () => {
						list.dataset.indicatorReady = "true";
					});
				}
			});
		}

		function applyNow() {
			if (destroyed) return;

			const triggers = node.querySelectorAll(".slex-tabs-trigger");
			const list = node.querySelector(".slex-tabs-list");
			let selectedTrigger;

			$.get(items).forEach((item, index) => {
				const trigger = triggers[index];

				if (!trigger) return;

				const itemValue = item.value ?? item.label;
				const selected = text(itemValue) === text($.get(value));

				trigger.dataset.value = text(itemValue);
				trigger.toggleAttribute("data-selected", selected);
				trigger.classList.toggle("slex-tabs-trigger--selected", selected);
				trigger.setAttribute("aria-selected", String(selected));

				if (selected) selectedTrigger = trigger;
			});

			if (list) scheduleIndicatorUpdate(list, selectedTrigger, $.get(orientation));
		}

		function apply() {
			if (destroyed) return;

			applyNow();

			queueMicrotask(() => {
				if (!destroyed) applyNow();
			});
		}

		apply();

		const list = node.querySelector(".slex-tabs-list");

		if (list && "ResizeObserver" in window) {
			resizeObserver = new ResizeObserver(apply);
			resizeObserver.observe(list);
		}

		return {
			update() {
				apply();
			},

			destroy() {
				destroyed = true;
				cancelScheduledFrame($$props.ctx, frame);
				resizeObserver?.disconnect();
			}
		};
	}

	var div = root_4();
	var div_1 = $.child(div);
	var node_1 = $.child(div_1);

	$.each(node_1, 17, () => $.get(items), $.index, ($$anchor, item, index) => {
		const itemValue = $.derived(() => $.get(item).value ?? $.get(item).label);
		const selected = $.derived(() => text($.get(itemValue)) === text($.get(value)));
		const hasIcon = $.derived(() => !!text($.get(item).icon));
		const label = $.derived(() => text($.get(item).label ?? $.get(itemValue)));
		const itemIconOnly = $.derived(() => !!$.get(item).iconOnly || $.get(hasIcon) && !$.get(item).label);

		const iconClass = $.derived(() => $.get(itemIconOnly)
			? "slex-tabs-trigger--icon"
			: $.get(hasIcon) ? "slex-tabs-trigger--with-icon" : "");

		var button = root_2();
		var node_2 = $.child(button);

		{
			var consequent = ($$anchor) => {
				InlineIcon($$anchor, {
					get name() {
						return $.get(item).icon;
					},

					get selected() {
						return $.get(selected);
					},
					className: 'slex-tabs-trigger-icon',
					onIconLoad: requestIndicatorUpdate
				});
			};

			$.if(node_2, ($$render) => {
				if ($.get(hasIcon)) $$render(consequent);
			});
		}

		var node_3 = $.sibling(node_2, 2);

		{
			var consequent_1 = ($$anchor) => {
				var span = root_1();
				var text_1 = $.child(span, true);

				$.reset(span);
				$.template_effect(() => $.set_text(text_1, $.get(label)));
				$.append($$anchor, span);
			};

			var alternate = ($$anchor) => {
				var text_2 = $.text();

				$.template_effect(() => $.set_text(text_2, $.get(label)));
				$.append($$anchor, text_2);
			};

			$.if(node_3, ($$render) => {
				if ($.get(itemIconOnly)) $$render(consequent_1); else $$render(alternate, -1);
			});
		}

		$.reset(button);

		$.template_effect(
			($0, $1) => {
				$.set_attribute(button, 'id', `slex-tab-${$$props.ctx.id ?? "tabs"}-${index}`);
				$.set_attribute(button, 'aria-controls', `slex-tabpanel-${$$props.ctx.id ?? "tabs"}-${index}`);
				$.set_attribute(button, 'aria-selected', $.get(selected));
				$.set_attribute(button, 'tabindex', $.get(selected) ? 0 : -1);
				button.disabled = !!$.get(item).disabled;
				$.set_attribute(button, 'data-value', $0);
				$.set_attribute(button, 'data-disabled', $.get(item).disabled ? "" : undefined);
				$.set_attribute(button, 'data-selected', $.get(selected) ? "" : undefined);
				$.set_class(button, 1, $1);
			},
			[
				() => text($.get(itemValue)),
				() => $.clsx(`${triggerClass} ${$.get(selected) ? "slex-tabs-trigger--selected" : ""} ${$.get(iconClass)}`.trim())
			]
		);

		$.delegated('click', button, () => chooseIndex(index));
		$.delegated('keydown', button, (event) => handleKeydown(event, index));
		$.append($$anchor, button);
	});

	$.next(2);
	$.reset(div_1);

	var node_4 = $.sibling(div_1, 2);

	$.each(node_4, 17, () => $.get(items), $.index, ($$anchor, item, index) => {
		const itemValue = $.derived(() => $.get(item).value ?? $.get(item).label);
		const selected = $.derived(() => text($.get(itemValue)) === text($.get(value)));
		var fragment_2 = $.comment();
		var node_5 = $.first_child(fragment_2);

		{
			var consequent_2 = ($$anchor) => {
				var div_2 = root_3();

				$.action(div_2, ($$node, $$action_arg) => renderTabPanel?.($$node, $$action_arg), () => $.get(item));

				$.template_effect(() => {
					$.set_attribute(div_2, 'id', `slex-tabpanel-${$$props.ctx.id ?? "tabs"}-${index}`);
					$.set_attribute(div_2, 'aria-labelledby', `slex-tab-${$$props.ctx.id ?? "tabs"}-${index}`);
				});

				$.append($$anchor, div_2);
			};

			$.if(node_5, ($$render) => {
				if ($.get(selected) && $.get(item).content) $$render(consequent_2);
			});
		}

		$.append($$anchor, fragment_2);
	});

	$.reset(div);

	$.action(div, ($$node, $$action_arg) => annotateTabs?.($$node, $$action_arg), () => ({
		value: $.get(value),
		items: $.get(items),
		iconVersion: $.get(iconVersion)
	}));

	$.template_effect(() => {
		$.set_attribute(div, 'data-orientation', $.get(orientation));
		$.set_attribute(div_1, 'aria-orientation', $.get(orientation));
	});

	$.append($$anchor, div);
	$.pop();
}

$.delegate(['click', 'keydown']);